import { useEffect, useMemo, useState } from 'react';
import ScopeCoverageSection from '../components/security/ScopeCoverageSection';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack, Paper, Grid,
  IconButton, Tooltip, Divider, Link, LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// 보안 취약점 현황 (Security Posture) — rederi 가 관리하는 모든 제품의 Dependabot
// 취약점을 포트폴리오 차원에서 한 화면에. 데이터: GET /api/security/portfolio
//   ① 전사 취약점 총량과 심각도 구성  ② 시간에 따른 추이(트렌드)
//   ③ 어느 제품이 가장 위험한가(순위)  ④ 당장 손봐야 할 상위 알림.

const SEVS = ['critical', 'high', 'medium', 'low'];
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
// 트렌드 막대 최대 높이(px). 퍼센트 높이 체인은 조상 높이 해석이 어긋나 막대가 폭주하므로
// 픽셀 고정으로 계산 — maxTrend 대비 비율을 곱해 결정적(deterministic)으로 높이를 준다.
const BAR_H = 150;

// 심각도 색 — 의미색 고정 팔레트. 라이트/다크 공통으로 대비 유지.
function sevColor(theme, sev) {
  const p = theme.palette;
  switch (sev) {
    case 'critical': return p.error.dark || '#b71c1c';
    case 'high': return p.error.main;
    case 'medium': return p.warning.main;
    case 'low': return p.info?.main || p.text.secondary;
    default: return p.text.secondary;
  }
}

// 제품 건강도 밴드 → 색·라벨.
function bandMeta(theme, b) {
  switch (b) {
    case 'critical': return { color: theme.palette.error.dark || '#b71c1c', label: 'Critical' };
    case 'high': return { color: theme.palette.error.main, label: 'High' };
    case 'medium': return { color: theme.palette.warning.main, label: 'Medium' };
    case 'low': return { color: theme.palette.info?.main || theme.palette.text.secondary, label: 'Low' };
    case 'clean': return { color: theme.palette.success.main, label: 'Clean' };
    case 'disabled': return { color: theme.palette.text.disabled, label: 'Dependabot Off' };
    default: return { color: theme.palette.text.disabled, label: '스냅샷 없음' };
  }
}

const num = (n) => (n ?? 0).toLocaleString();
const fmtUTC = (iso) => (iso ? `${String(iso).slice(0, 10)}` : '—');

function relTime(iso) {
  if (!iso) return '기록 없음';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.round(diff / 3_600_000);
  if (h < 1) return '방금';
  if (h < 48) return `${h}시간 전`;
  return `${Math.round(h / 24)}일 전`;
}

// 전사 수집 상태 — 스냅샷 최신성으로 판정.
function ingestStatus(iso, missing) {
  if (!iso) return { label: '데이터 없음', color: 'error' };
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (missing > 0) return { label: `${missing}개 제품 누락`, color: 'warning' };
  if (h < 30) return { label: '수집 정상', color: 'success' };
  return { label: '수집 지연', color: 'warning' };
}

export default function SecurityPage() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetch('/api/security/portfolio')
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error || body?.detail || `HTTP ${r.status}`);
        return body;
      })
      .then(setData)
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const status = ingestStatus(data?.generated_at, data?.snapshots_missing);
  const totals = data?.totals || {};
  const trend = data?.trend || [];
  const maxTrend = Math.max(1, ...trend.map((t) => t.total));

  const products = data?.products || [];
  const alerts = data?.top_alerts || [];
  const packages = data?.top_packages || [];
  const maxPkg = Math.max(1, ...packages.map((p) => p.total));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      {/* 헤더 */}
      <Stack direction="row" flexWrap="wrap" alignItems="flex-start" justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🛡️ 보안 취약점 현황
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '68ch' }}>
            rederi 가 관리하는 <b>모든 제품</b>의 Dependabot 취약점을 한 화면에서 봅니다 —
            {' '}전사 총량과 심각도 구성, <b>시간에 따른 추이</b>, 제품별 위험 순위, 당장 손봐야 할 상위 알림.
          </Typography>
        </Box>
        <Stack alignItems="flex-end" gap={0.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              size="small"
              color={status.color}
              variant="outlined"
              icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
              label={status.label}
              sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}
            />
            <Tooltip title="새로고침" arrow>
              <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            스냅샷 {data?.snapshots_found ?? 0}/{data?.products_count ?? 0} · {relTime(data?.generated_at)}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          보안 현황 조회 실패: {error}
          <br />
          <Typography variant="caption">
            GitHub 토큰(GH_PAT) 미설정이거나 스냅샷 파일에 접근할 수 없습니다 — Cloudflare Pages env 확인.
          </Typography>
        </Alert>
      )}

      {data && (
        <Stack gap={2}>
          {/* KPI 스트립 */}
          <Grid container spacing={2}>
            <Kpi title="총 취약점" value={num(totals.total)} why="전 제품 open Dependabot alert 합계." />
            <Kpi title="Critical" value={num(totals.critical)} why="즉시 조치 대상. 0이 목표." accent={sevColor(theme, 'critical')} />
            <Kpi title="High" value={num(totals.high)} why="우선 조치 대상." accent={sevColor(theme, 'high')} />
            <Kpi title="영향받는 제품" value={`${num(data.products_with_alerts)} / ${num(data.products_count)}`} why="취약점이 1건 이상인 제품 수." />
            <Kpi title="Dependabot 커버리지" value={`${num(data.dependabot_enabled)} / ${num(data.products_count)}`} why="alert 수집이 켜진 제품. 낮으면 사각지대." />
          </Grid>

          {/* 포트폴리오 트렌드 — 심각도 누적 막대 */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
              <Typography variant="h4">📈 전사 취약점 추이</Typography>
              <Chip label={`최근 ${trend.length}일`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'primary.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
              날짜별 전 제품 취약점 총량을 심각도별로 쌓아 표시. <b>막대가 우상향이면 부채가 쌓이는 중</b>,
              {' '}빨강(Critical/High) 비중이 커지면 위험이 심화되는 신호. (최근 30일 · 수집 시작 전 구간은 첫 관측값으로 보정)
            </Typography>

            {trend.length === 0 ? (
              <Typography variant="body2" color="text.secondary">히스토리 데이터 없음.</Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: BAR_H + 22, pt: 1 }}>
                  {trend.map((t) => {
                    const topSev = SEVS.find((s) => t[s] > 0); // 최상단(비-0) 심각도 — 둥근 모서리용
                    return (
                      <Tooltip
                        key={t.date}
                        arrow
                        title={`${t.date} · 총 ${num(t.total)} (C${t.critical} H${t.high} M${t.medium} L${t.low})`}
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
                          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: BAR_H }}>
                            {SEVS.map((s) => {
                              const px = (t[s] / maxTrend) * BAR_H;
                              if (px <= 0) return null;
                              return (
                                <Box
                                  key={s}
                                  sx={{
                                    width: '100%',
                                    height: `${px}px`,
                                    bgcolor: sevColor(theme, s),
                                    borderRadius: s === topSev ? '3px 3px 0 0' : 0,
                                  }}
                                />
                              );
                            })}
                          </Box>
                          <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, textAlign: 'center', mt: 0.5 }} color="text.secondary">
                            {t.date.slice(5)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
                <Stack direction="row" gap={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                  {SEVS.map((s) => (
                    <Stack key={s} direction="row" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: sevColor(theme, s) }} />
                      <Typography variant="caption" color="text.secondary">{SEV_LABEL[s]}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Paper>

          <Grid container spacing={2}>
            {/* 제품별 위험 순위 */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h4" gutterBottom>🎯 제품별 위험 순위</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'error.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
                  심각도 가중치 순 정렬. <b>Critical 이 있는 제품이 최상단</b>. 우측 GitHub 링크로 해당 제품의 Dependabot 알림으로 바로 이동.
                </Typography>

                <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                  {products.map((p) => {
                    const bm = bandMeta(theme, p.band);
                    const c = p.current || {};
                    return (
                      <Box key={p.id} sx={{ py: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: bm.color, flex: 'none' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', flex: 'none' }}>
                              {p.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                            >
                              {p.id}{p.missing ? ' · 스냅샷 없음' : p.dependabot_enabled === false ? ' · Dependabot off' : ''}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" gap={0.75} sx={{ flex: 'none' }}>
                            {SEVS.map((s) => (
                              (c[s] > 0) ? (
                                <Tooltip key={s} title={SEV_LABEL[s]} arrow>
                                  <Box sx={{
                                    fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700,
                                    color: '#fff', bgcolor: sevColor(theme, s), px: 0.6, py: 0.1, borderRadius: 0.5, minWidth: 18, textAlign: 'center',
                                  }}>
                                    {c[s]}
                                  </Box>
                                </Tooltip>
                              ) : null
                            ))}
                            {(!p.missing && (c.total ?? 0) === 0) && (
                              <Chip label="clean" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, color: 'success.main', borderColor: 'success.main' }} />
                            )}
                            <Link
                              href={`https://github.com/rayrndsw/${p.id}/security/dependabot`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 15 }} />
                            </Link>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>

            {/* 심각도 구성 도넛 대체 — 심각도별 막대 */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h4" gutterBottom>🧮 심각도 구성</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'warning.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
                  전 제품 취약점의 심각도 분포. Critical/High 비중이 조치 우선순위를 좌우.
                </Typography>
                <Stack gap={1.5}>
                  {SEVS.map((s) => {
                    const v = totals[s] || 0;
                    const pct = totals.total ? Math.round((v / totals.total) * 100) : 0;
                    return (
                      <Box key={s}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{SEV_LABEL[s]}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }} color="text.secondary">
                            <b>{num(v)}</b> · {pct}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{ height: 10, borderRadius: 5, bgcolor: 'action.hover', [`& .MuiLinearProgress-bar`]: { bgcolor: sevColor(theme, s) } }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">
                    전사 <b>{num(totals.total)}</b>건 중 즉시 조치 대상(Critical+High)이
                    {' '}<b style={{ color: sevColor(theme, 'critical') }}>{num((totals.critical || 0) + (totals.high || 0))}</b>건.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* 취약 패키지 Top 10 — 심각도 구성 카드 아래 */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h4" gutterBottom>📦 취약 패키지 Top 10</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'warning.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
              여러 제품에서 반복 등장하는 취약 의존성을 개수로 정렬. <b>하나만 올려도 여러 제품의 심각 취약점이 사라진다</b> — 조치 레버리지 큰 순.
              {' '}(추적 대상 Critical/High 알림 기준 · repo당 최대 20건이라 실제 개수의 하한값)
            </Typography>
            {packages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">집계할 패키지 없음.</Typography>
            ) : (
              <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                {packages.map((p, i) => (
                  <Box key={p.package} sx={{ display: 'grid', gridTemplateColumns: '22px minmax(110px, 1fr) auto', alignItems: 'center', gap: 1.5, py: 0.85 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.disabled', textAlign: 'right' }}>{i + 1}</Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>{p.package}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.product_count}개 제품</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(p.total / maxPkg) * 100}
                        sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'action.hover', [`& .MuiLinearProgress-bar`]: { bgcolor: p.critical > 0 ? sevColor(theme, 'critical') : sevColor(theme, 'high') } }}
                      />
                    </Box>
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ flex: 'none' }}>
                      {SEVS.map((s) => (p[s] > 0 ? (
                        <Box
                          key={s}
                          sx={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: '#fff',
                            bgcolor: sevColor(theme, s), px: 0.7, py: 0.15, borderRadius: 0.5, whiteSpace: 'nowrap',
                          }}
                        >
                          {{ critical: 'CRIT', high: 'HIGH', medium: 'MED', low: 'LOW' }[s]}:{p[s]}
                        </Box>
                      ) : null))}
                      <Box sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700, minWidth: 30, textAlign: 'right' }}>{p.total}</Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          {/* 상위 알림 — 당장 손봐야 할 것 */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h4" gutterBottom>🚨 상위 취약점 알림</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'error.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
              전 제품 알림 중 Critical/High 우선, 최신순. 패키지·CVE·제품을 한 줄로. 클릭 시 GitHub 알림 상세로 이동.
            </Typography>
            {alerts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">표시할 상위 알림 없음 (또는 스냅샷에 top_alerts 미포함).</Typography>
            ) : (
              <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                {alerts.map((a, i) => (
                  <Stack key={`${a.product_id}-${a.ghsa || i}`} direction="row" alignItems="flex-start" gap={1.25} sx={{ py: 1 }}>
                    <Box sx={{
                      fontFamily: 'JetBrains Mono, monospace', fontSize: 10, fontWeight: 700, color: '#fff',
                      bgcolor: sevColor(theme, String(a.severity)), px: 0.7, py: 0.2, borderRadius: 0.5, mt: 0.2, flex: 'none', textTransform: 'uppercase',
                    }}>
                      {String(a.severity || '?').slice(0, 4)}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>{a.package || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.product_name}</Typography>
                        {a.created_at && <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtUTC(a.created_at)}</Typography>}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                        {a.summary || a.ghsa || ''}
                      </Typography>
                    </Box>
                    {a.url && (
                      <Link href={String(a.url)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', color: 'text.disabled', mt: 0.3, '&:hover': { color: 'primary.main' } }}>
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                      </Link>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, pt: 2, borderTop: 1, borderColor: 'divider', lineHeight: 1.7 }}>
            <b>이 화면이 답하는 질문</b> — ① 전사 취약점이 얼마나 되나 · ② 시간에 따라 늘고 있나(트렌드) · ③ 어느 제품이 가장 위험한가 · ④ 당장 무엇부터 손봐야 하나.
            <br />
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              source: 각 제품 repo 의 security/vuln-snapshot.json (GitHub Dependabot · 일 1회 갱신) · 요청 시점 집계 · edge cache 30분
            </span>
          </Typography>
        </Stack>
      )}

      {/* 🔴 위는 "취약점이 몇 건인가"(Dependabot), 여기는 "그걸 세는 대상이 맞나"(Dependency-Track).
             두 수치가 다른 것이 정상이다 — 도구도 범위도 다르다. 같아 보이게 만들면 거짓말이 된다.
             조회가 분리돼 있어 위쪽 집계가 실패해도 이 섹션은 정상 동작한다. */}
      <ScopeCoverageSection />
    </Box>
  );
}

function Kpi({ title, value, unit, why, accent }) {
  return (
    <Grid item xs={6} md={2.4}>
      <Paper variant="outlined" sx={{ p: 1.75, height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 24, fontWeight: 600, lineHeight: 1.2, my: 0.25, color: accent || 'text.primary' }}>
          {value} {unit && <Typography component="span" sx={{ fontSize: 13 }} color="text.secondary">{unit}</Typography>}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{why}</Typography>
      </Paper>
    </Grid>
  );
}
